import { useEffect, useRef } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import type { UserGamificationSummary } from '@evi/shared';
import { fetchUserSummary, fetchGamificationHistory } from '@/lib/api';

/**
 * React-Query hook for the driver gamification summary.
 *
 * 30s `staleTime` — Ticket 2's acceptance criterion is "no cache
 * staleness longer than 30s" so a freshly-completed session shows
 * up on the rewards page within half a minute. Daily-checkin /
 * referral / coupon mutations also invalidate this query for an
 * instant refresh on the same tab.
 *
 * Also wires dev-mode-only diagnostic logging for the badge-flicker
 * bug Warren reported in stage. The frontend code path is clean —
 * the hook returns whatever the server delivers — so a "badges
 * appeared then disappeared" observation almost certainly means
 * the server returned an actually-shorter `earnedBadges` list on a
 * subsequent fetch. The shrink-detection log captures the before /
 * after lists + a timestamp the next time it happens, so the bug
 * can be triaged with concrete data instead of by re-deriving the
 * code path each time. Production users never see the log; the
 * import.meta.env.DEV gate is statically false in vite production
 * builds and tree-shakes out.
 */
export function useUserSummary() {
  const query = useQuery<UserGamificationSummary>({
    queryKey: ['gamification', 'user-summary'],
    queryFn: fetchUserSummary,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  // Track the last seen earnedBadges so we can detect a shrink
  // between renders. Using a ref (not state) so the comparison
  // doesn't itself trigger re-renders.
  const previousBadgesRef = useRef<UserGamificationSummary['earnedBadges'] | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const next = query.data?.earnedBadges;
    if (!next) return;
    const prev = previousBadgesRef.current;
    if (prev && next.length < prev.length) {
      // Truncate the printed arrays so the console isn't flooded
      // when a user actually owns 30+ badges. The first 5 ids on
      // each side are usually enough to fingerprint which badges
      // disappeared.
      const summarize = (
        rows: UserGamificationSummary['earnedBadges'],
      ): Array<string> => rows.slice(0, 5).map((b) => b.code);
      console.warn(
        '[useUserSummary] earnedBadges shrunk — likely the flicker bug. '
        + 'If you can reproduce, capture this log + the user-summary network response.',
        {
          timestamp: new Date().toISOString(),
          previousCount: prev.length,
          newCount: next.length,
          previousFirstFive: summarize(prev),
          newFirstFive: summarize(next),
        },
      );
    }
    previousBadgesRef.current = next;
  }, [query.data]);

  return query;
}

/**
 * Cursor-paginated reward history feed for the dedicated
 * /rewards/history page. Replaces the prior "fetch the same 20
 * rows from useUserSummary" hack so drivers with > 20 events
 * can page through their full ledger.
 *
 * Uses React Query v5's useInfiniteQuery — the library handles
 * pagination state machine (pages array, fetchNextPage,
 * isFetchingNextPage, hasNextPage). Caller flattens via
 * data.pages.flatMap(p => p.entries).
 *
 * Page size: 50. The /rewards parent page's truncated 5-row
 * display reads from useUserSummary instead — no need to
 * paginate for a 5-row preview.
 *
 * Mirrors the existing FriendsPage ActivityFeedTab shape so
 * the codebase has one consistent pattern for cursor-paginated
 * feeds.
 */
const HISTORY_PAGE_SIZE = 50;

export function useGamificationHistory() {
  return useInfiniteQuery({
    queryKey: ['gamification', 'history'],
    queryFn: ({ pageParam }) => fetchGamificationHistory({
      limit: HISTORY_PAGE_SIZE,
      ...(pageParam ? { before: pageParam } : {}),
    }),
    initialPageParam: null as string | null,
    // React Query stops paginating when getNextPageParam returns
    // undefined; null next_cursor from server (end-of-feed)
    // collapses to undefined here.
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // 30s staleTime matches useUserSummary — recent events show
    // up within half a minute on the next page-mount or window
    // refocus; mutation invalidations (manual adjustment, etc.)
    // override.
    staleTime: 30_000,
  });
}
